import React from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { palette } from '../../../utils/designTokens';
import { tint } from './rcaHelpers';

export function FeedbackGlyph({ type, active = false, size = 14 }) {
  const Icon = type === 'down' ? ThumbsDown : ThumbsUp;
  return (
    <span
      className="inline-flex items-center justify-center rounded-full"
      style={{
        width: size + 8,
        height: size + 8,
        backgroundColor: active ? 'currentColor' : tint('currentColor', 8),
      }}
    >
      <Icon
        size={size}
        strokeWidth={2.2}
        style={{ color: active ? palette.neutrals.canvas : 'currentColor' }}
      />
    </span>
  );
}

export function Section({ title, children, action = null }) {
  return (
    <section className="rounded-lg border border-border-subtle bg-background-secondary p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function TextList({ items }) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) return <div className="text-sm text-text-muted">No details available.</div>;
  return (
    <ol className="space-y-2">
      {rows.map((item, idx) => (
        <li key={idx} className="text-sm leading-relaxed text-text-primary">
          <span className="mr-2 text-text-muted">{idx + 1}.</span>{String(item)}
        </li>
      ))}
    </ol>
  );
}

export function DetailList({ items }) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) return <div className="text-sm text-text-muted">No details available.</div>;
  return (
    <div className="space-y-2">
      {rows.map((item, idx) => {
        if (!item || typeof item !== 'object') {
          return (
            <div key={idx} className="text-sm leading-relaxed text-text-primary">
              {String(item)}
            </div>
          );
        }
        const title = item.claim || item.what_happened || item.title || item.span_id || `Item ${idx + 1}`;
        const detail = item.outcome || item.reason || item.detail || '';
        const isEvidence = item.role === 'evidence';
        const itemStyle = isEvidence
          ? {
            borderColor: tint(palette.status.danger, 45),
            backgroundColor: tint(palette.status.danger, 8),
          }
          : undefined;
        return (
          <div key={idx} className="rounded border border-border-subtle bg-background-primary px-3 py-2" style={itemStyle}>
            <div className="text-sm text-text-primary">{title}</div>
            {detail && <div className="mt-1 text-xs text-text-muted">{detail}</div>}
            {item.span_id && <div className="mt-1 font-mono text-[11px] text-text-muted">{item.span_id}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function KeyValueGrid({ value }) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '');
  if (!entries.length) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {entries.map(([key, item]) => (
        <div key={key} className="rounded border border-border-subtle bg-background-primary px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            {key.replace(/_/g, ' ')}
          </div>
          <div className="mt-1 break-words text-sm text-text-primary">
            {typeof item === 'object' ? JSON.stringify(item) : String(item)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FixList({ fixes }) {
  const rows = Array.isArray(fixes) ? fixes : [];
  if (!rows.length) return <div className="text-sm text-text-muted">No fixes returned.</div>;
  return (
    <div className="space-y-2">
      {rows.map((fix, idx) => (
        <div key={idx} className="rounded border border-border-subtle bg-background-primary px-3 py-2">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            {fix?.owner || 'unknown owner'}
          </div>
          <div className="text-sm text-text-primary">{fix?.fix || JSON.stringify(fix)}</div>
          {fix?.rationale && <div className="mt-1 text-xs text-text-muted">{fix.rationale}</div>}
        </div>
      ))}
    </div>
  );
}
